/**
 * Reviews (V2, Phase 11.5.7 — full literal clone). ONE component per kind, composed from
 * the Production Foundation and ported as a PIXEL-PERFECT literal clone of the reference
 * `landing/components/review/landing-review.tsx`. The React tree mirrors the reference DOM
 * EXACTLY, with `.landing-row-reviews*` → `.sbp-reviews*`. Only CSS (reviews.css) changes
 * between the frozen layouts: columns1 | columns2 | columns3 | columns4 | carousel.
 *
 * Reference DOM (landing-review.tsx):
 *   <section .landing-row-reviews>
 *     <div .landing-row-reviews-info> <LandingViewInfo/> </div>
 *     <ul .landing-row-reviews-list _N>
 *       <li> <div .landing-row-reviews-item>
 *              <div>★×5</div> <p>{content}</p>
 *              <div .landing-row-reviews-user> <img/> <span>{name}</span> </div>
 *            </div> </li>
 *     </ul>
 *     <LandingAction/>
 *   </section>
 *
 * The reference band `.landing-row.reviews` is #f8f8f8 → Section tone="alt". Public API
 * unchanged (SectionComponentProps<'reviews'>); the review-card DOM is inlined (no ReviewCard).
 *
 * The card list is a CSS GRID (reviews.css), not the reference's `column-count` masonry:
 * multi-column layout fills top-to-bottom WITHIN a column before moving to the next one, so
 * appending a new review to the end of `items` could visually land it mid-grid instead of
 * after the last card — grid fills strictly left-to-right, then top-to-bottom, in DOM order,
 * so the Nth item always renders as the Nth visual card, matching Add/Delete/Reorder exactly.
 *
 * The reviewer avatar (`.sbp-reviews__user`) uses a neutral silhouette (`Icon name="user"`)
 * when a review has no uploaded photo, instead of the shared `Image` component's generic
 * picture-icon placeholder — a person-shaped placeholder reads correctly here without
 * changing the generic image placeholder other sections (Services, Gallery) rely on.
 */
import type { SectionComponentProps } from '../../runtime/types'
import { Section, InfoBlock, Actions, Image, Icon, cx } from '../shared'
import './reviews.css'

export function Reviews(p: SectionComponentProps<'reviews'>) {
  const { content, layout, id, theme, locale, assets } = p

  const titleId = `${id}-title`
  const items = content.items ?? []

  return (
    <Section
      as="section"
      tone="alt"
      theme={theme}
      locale={locale}
      id={id}
      ariaLabelledBy={content.heading ? titleId : undefined}
      className={cx('sbp-reviews', `sbp-reviews--${layout}`)}
    >
      <div className="sbp-reviews__inner">
        {/* .landing-row-reviews-info → centered InfoBlock (no actions here) */}
        <div className="sbp-reviews__info">
          <InfoBlock
            eyebrowBar
            eyebrow={content.eyebrow}
            heading={content.heading}
            subheading={content.subheading}
            headingLevel={2}
            headingId={titleId}
            align="center"
          />
        </div>

        {/* .landing-row-reviews-list → CSS-columns masonry (layout drives column-count) */}
        <ul className="sbp-reviews__list">
          {items.map((item) => {
            const rating = item.rating ?? 0
            return (
              <li key={item.id}>
                <div className="sbp-reviews__card">
                  <div className="sbp-reviews__stars">
                    {[...Array(5)].map((_, index) => {
                      const value = index + 1
                      return (
                        <span
                          key={value}
                          className={cx('sbp-reviews__star', value <= rating && 'active')}
                        >
                          ★
                        </span>
                      )
                    })}
                  </div>
                  <p>{item.description ?? ''}</p>
                  <div className="sbp-reviews__user">
                    {item.media ? (
                      <Image asset={assets.resolve(item.media.assetId)} alt="" />
                    ) : (
                      <span className="sbp-reviews__avatar-placeholder" aria-hidden="true">
                        <Icon name="user" size={24} />
                      </span>
                    )}
                    <span>{item.title}</span>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>

        {/* .landing-row-reviews .landing-buttons-container → centered Actions */}
        <Actions actions={content.actions} align="center" size="lg" />
      </div>
    </Section>
  )
}
